import * as THREE from 'three'
import { GLTFLoader }       from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader }      from 'three/addons/loaders/DRACOLoader.js'
import { OrbitControls }    from 'three/addons/controls/OrbitControls.js'
import { RoomEnvironment }  from 'three/addons/environments/RoomEnvironment.js'
import Lenis               from 'lenis'

// ─── Data ──────────────────────────────────────────────────────────────────

// Each ring model is hosted on Shopify's CDN under its own hashed path (not a
// predictable pattern), so shape_shank combos are looked up here rather than
// built from a template string.
const GLB_URLS = {
  emrald_channel:   'https://cdn.shopify.com/3d/models/df36518138172ebc/AI_BYOR_emrald_channel.glb',
  emrald_cave:      'https://cdn.shopify.com/3d/models/04a36ad637e23f85/AI_BYOR_emrald_cave.glb',
  emrald_classic:   'https://cdn.shopify.com/3d/models/50c127dc2ee7df4d/AI_BYOR_emrald_classic.glb',
  emrald_twisted:   'https://cdn.shopify.com/3d/models/3b66a66b9691e000/AI_BYOR_emrald_twisted.glb',
  emrald_split:     'https://cdn.shopify.com/3d/models/5a684436a8ab605a/AI_BYOR_emrald_split.glb',

  pearl_channel:    'https://cdn.shopify.com/3d/models/7c9a0fdf968a0605/AI_BYOR_pearl_channel.glb',
  pearl_cave:       'https://cdn.shopify.com/3d/models/246163cc6b78f63c/AI_BYOR_pearl_cave.glb',
  pearl_classic:    'https://cdn.shopify.com/3d/models/7b5177c2e37e34c3/AI_BYOR_pearl_classic.glb',
  pearl_twisted:    'https://cdn.shopify.com/3d/models/c5907ef67caa5b0c/AI_BYOR_pearl_twisted.glb',
  pearl_split:      'https://cdn.shopify.com/3d/models/9b3bad6608500f87/AI_BYOR_pearl_split.glb',

  princess_channel: 'https://cdn.shopify.com/3d/models/323eb677a8d019a8/AI_BYOR_princess_channel.glb',
  princess_cave:    'https://cdn.shopify.com/3d/models/cf6c234597a357b5/AI_BYOR_princess_cave.glb',
  princess_classic: 'https://cdn.shopify.com/3d/models/155d722a348d5d8c/AI_BYOR_princess_classic.glb',
  princess_twisted: 'https://cdn.shopify.com/3d/models/432055264effd030/AI_BYOR_princess_twisted.glb',
  princess_split:   'https://cdn.shopify.com/3d/models/c027bf133234786d/AI_BYOR_princess_split.glb',

  round_channel:    'https://cdn.shopify.com/3d/models/589009e7ad75f663/AI_BYOR_round_channel.glb',
  round_cave:       'https://cdn.shopify.com/3d/models/0b0153fb6d9243e5/AI_BYOR_round_cave.glb',
  round_classic:    'https://cdn.shopify.com/3d/models/09d412a8a20f165d/AI_BYOR_round_classic.glb',
  round_twisted:    'https://cdn.shopify.com/3d/models/1536552eaaffbf07/AI_BYOR_round_twisted.glb',
  round_split:      'https://cdn.shopify.com/3d/models/533a7e8ce9e837e5/AI_BYOR_round_split.glb',

  vintage_channel:  'https://cdn.shopify.com/3d/models/ae19a160cad094a5/AI_BYOR_vintage_channel.glb',
  vintage_cave:     'https://cdn.shopify.com/3d/models/8787442ee84ab363/AI_BYOR_vintage_cave.glb',
  vintage_classic:  'https://cdn.shopify.com/3d/models/bd7326e22949041c/AI_BYOR_vintage_classic.glb',
  vintage_twisted:  'https://cdn.shopify.com/3d/models/624a6151d3b4fd1a/AI_BYOR_vintage_twisted.glb',
  vintage_split:    'https://cdn.shopify.com/3d/models/5123d4e47d425fc3/AI_BYOR_vintage_split.glb',
}

// Yellow/White/Rose Gold values (color, roughness, envMap) sourced directly
// from src/BYORUpdated 3.vjson's material definitions (the "Shank" polished
// variants — the "Head" variants in that file have metalness:0, which
// wouldn't render as metal at all, so they're treated as a placeholder).
// Platinum has no equivalent entry in that file, so it's left as before.
const METALS = [
  { id: 'yellow-gold', label: 'Yellow Gold', color: '#E3BB5E', roughness: 0, envMap: 0.8,
    dot: 'linear-gradient(135deg,#7D6734 0%,#E3BB5E 38%,#F2E0B7 54%,#E3BB5E 70%,#7D6734 100%)' },
  { id: 'white-gold',  label: 'White Gold',  color: '#C2C2C3', roughness: 0, envMap: 1,
    dot: 'linear-gradient(135deg,#6B6B6B 0%,#C2C2C3 38%,#E4E4E4 54%,#C2C2C3 70%,#6B6B6B 100%)' },
  { id: 'rose-gold',   label: 'Rose Gold',   color: '#F2AF83', roughness: 0, envMap: 1,
    dot: 'linear-gradient(135deg,#856048 0%,#F2AF83 38%,#F9DBC7 54%,#F2AF83 70%,#856048 100%)' },
  { id: 'platinum',    label: 'Platinum',    color: '#C4C4CE', roughness: 0.06, envMap: 1.6,
    dot: 'linear-gradient(135deg,#525265 0%,#9898AC 38%,#D6D6E4 54%,#9898AC 70%,#525265 100%)' },
]

const SEC2_SHAPES = [
  { id: 'round',    label: 'Round',    desc: 'Round Brilliant' },
  { id: 'pearl',    label: 'Pear',     desc: 'Pear Shape'      },
  { id: 'emrald',   label: 'Emerald',  desc: 'Step Cut'        },
  { id: 'princess', label: 'Princess', desc: 'Square Cut'      },
  { id: 'vintage',  label: 'Vintage',  desc: 'Vintage Round'   },
]

const SEC2_SHANKS = [
  { id: 'classic', label: 'Classic', desc: 'Simple Solitaire' },
  { id: 'channel', label: 'Channel', desc: 'Channel Set'      },
  { id: 'cave',    label: 'Cave',    desc: 'Cave Setting'     },
  { id: 'split',   label: 'Split',   desc: 'Split Shank'      },
  { id: 'twisted', label: 'Twisted', desc: 'Rope Twist'       },
]

const FOUR_CS = [
  { id: 'cut',     label: 'Cut',     tagline: 'Brilliance & Fire',  info: "The cut grade determines how well a diamond reflects light. Graded Poor to Excellent — an Excellent cut maximises brilliance, fire, and scintillation through precise facet angles and proportions." },
  { id: 'color',   label: 'Color',   tagline: 'Colorless to Tinted',info: "Graded D (colorless) to Z (light yellow). D–F are colorless and most prized; G–I are near-colorless and offer excellent value. The less colour present, the rarer the stone." },
  { id: 'clarity', label: 'Clarity', tagline: 'Purity Within',      info: "Graded IF (Internally Flawless) through VVS, VS, and SI. Natural characteristics — clouds, crystals, feathers, needles — are the diamond's fingerprint. VS2 and above are eye-clean." },
  { id: 'carat',   label: 'Carat',   tagline: 'Weight, Not Size',   info: "Carat measures weight (1ct = 0.2g), not physical size. Two equal-carat diamonds can look very different depending on cut and shape — cut quality matters more than carat weight alone." },
]

const METALS_EDU = [
  { id: 'yellow-gold', name: 'Yellow Gold', tagline: 'Timeless Warmth',
    desc: 'Naturally yellow, enriched with silver and copper alloys for durability. Higher karat means richer colour; 18K balances brilliance with everyday strength.',
    gradient: 'linear-gradient(145deg,#B8860B 0%,#DAA520 28%,#FFD700 50%,#DAA520 72%,#B8860B 100%)' },
  { id: 'white-gold',  name: 'White Gold',  tagline: "Platinum's Elegance",
    desc: "Gold alloyed with palladium or nickel, rhodium-plated for a bright silver-white lustre. Offers platinum's look at a more accessible price.",
    gradient: 'linear-gradient(145deg,#909098 0%,#C8C8D0 28%,#F2F2F6 50%,#C8C8D0 72%,#909098 100%)' },
  { id: 'rose-gold',   name: 'Rose Gold',   tagline: 'Romantic Blush',
    desc: 'Pure gold alloyed with copper for a warm pink hue that deepens with higher copper content. Complements all skin tones — the blush is entirely natural.',
    gradient: 'linear-gradient(145deg,#8B3A3A 0%,#B86868 28%,#E8ACA8 50%,#B86868 72%,#8B3A3A 100%)' },
  { id: 'platinum',    name: 'Platinum',    tagline: 'Rarest Strength',
    desc: 'Thirty times rarer than gold. Naturally white, hypoallergenic, never tarnishes, never needs re-plating. Its density holds stones more securely than any other metal.',
    gradient: 'linear-gradient(145deg,#626272 0%,#9898AC 28%,#D4D4E2 50%,#9898AC 72%,#626272 100%)' },
]

const FAQ_CATS = ['Ring Sizing', 'Pricing & Budget', 'Ring Styles', 'Studio & Visits', 'Metal & Care']

const FAQ_DATA = {
  'Ring Sizing': [
    { q: 'What is the ring size chart for India?', a: 'Rings & I follows Indian sizing (1–30), where size 16 ≈ 51.9 mm circumference. Size 12 is ~49.0 mm, size 20 is ~54.8 mm, and size 24 is ~57.8 mm. Professional sizing is available at our studios in Pune, Andheri, and Borivali.' },
    { q: 'How do I measure my ring size at home?', a: 'Three methods: wrap a string around your finger and divide the circumference by 3.14; measure the inner diameter of an existing ring; or use our online ring size calculator. For the most accurate result, visit a studio for professional sizing.' },
    { q: 'What ring size is 16 in Indian sizing?', a: 'Indian size 16 equals approximately 51.2 mm inner circumference — equivalent to US size 6 and UK size L½. It is the most common size for Indian women.' },
    { q: 'Can I get my ring size checked at Rings & I?', a: 'Yes — complimentary sizing at all three studios using professional ring gauges. Walk-ins are welcome; best measured mid-morning when fingers are at a neutral temperature.' },
    { q: 'How much does ring resizing cost?', a: 'First resizing is free on in-studio purchases. Subsequent resizing: ₹500–₹1,500 for gold; ₹1,000–₹2,500 for platinum. Intricate bands may cost more.' },
    { q: 'What is the difference between US and Indian ring sizes?', a: 'Both systems measure the same thing with different numbering. US size 5 ≈ Indian 10; US 7 ≈ Indian 15; US 9 ≈ Indian 19.' },
  ],
  'Pricing & Budget': [
    { q: 'What is the price of a solitaire ring in India?', a: 'Priced across 9 natural diamond tiers and 3 lab-grown tiers. Natural diamond solitaires in 14 KT gold start with the Delightful tier (IJ–SI clarity). Platinum options are also available, and all come with certification.' },
    { q: 'How much does a gold engagement ring cost in Mumbai?', a: 'Starting from approximately ₹18,000. Most couples choose between ₹25,000–₹1,50,000. Price varies by gold purity, weight, and stone specifications.' },
    { q: 'What is the price of a couple ring set?', a: 'Starting from ₹18,000 for both rings combined. The popular range is ₹25,000–₹60,000 with certified diamonds and BIS hallmarks.' },
    { q: 'Are lab-grown diamond rings cheaper than natural diamonds?', a: 'Lab-grown diamonds are more affordable due to controlled sourcing but are chemically identical to natural diamonds. All our lab-grown options use EF–VVS quality with IGI certification.' },
    { q: 'What is the making charge for gold rings in India?', a: 'Starting at 8% of gold value for plain bands. Designer and hand-crafted styles range 12–18% depending on complexity.' },
    { q: 'Can I buy a ring under ₹5,000 in India?', a: 'Yes — entry-level options are available in the Delightful and Radiant tiers in 14 KT gold across all 16 ring styles.' },
  ],
  'Ring Styles': [
    { q: 'What is a solitaire ring?', a: 'A single-stone design where one diamond or gemstone sits at the centre, unaccompanied. Available in classic four-prong, six-prong, and bezel settings. Prices typically start around ₹25,000.' },
    { q: 'What is the difference between a solitaire and a halo ring?', a: 'A solitaire features a single diamond — clean and timeless. A halo surrounds the centre diamond with smaller diamonds, creating a larger appearance at a similar price.' },
    { q: 'Which ring style is best for an engagement in India?', a: 'Solitaire and halo are the most popular choices. Solitaires suit minimalist brides; halos offer maximum sparkle. Three-stone and pavé styles are also available.' },
    { q: 'What is a wedding ring set?', a: 'A coordinated pair of engagement and wedding bands designed to sit flush together on the finger as a unified look. Typically starting from ₹25,000.' },
    { q: 'What is a promise ring?', a: 'A symbol of commitment before a formal engagement, representing love and loyalty. Promise rings are typically simpler and more affordable than engagement rings.' },
    { q: 'Which finger is the engagement ring worn on in India?', a: 'The right-hand ring finger, which is the traditional wearing finger for engagement rings in most Indian communities. Some South Indian traditions wear it on the right hand permanently.' },
  ],
  'Studio & Visits': [
    { q: 'Where are Rings & I stores located?', a: 'Pune: 301, 3rd Floor Silver Mist, Sadashiv Peth. Andheri: Royal Classic, New Link Road. Borivali: Megh CHS, Factory Lane. Virtual consultations are also available for customers across India and internationally.' },
    { q: 'How do I book a ring fitting appointment?', a: 'Book via WhatsApp, phone, or our online form. Choose your preferred studio and time slot. Walk-ins are welcome in Pune; Mumbai studios require an appointment.' },
    { q: 'Does Rings & I offer virtual consultations?', a: 'Yes — free 30-minute consultations are available 7 days a week for customers across India and internationally. Visit ringsandi.com/pages/virtual-appointment to schedule.' },
    { q: 'Can I customise a ring at Rings & I?', a: 'Full customisation is available — metal tone, gold purity, diamond clarity and shape, ring finish, and inside engraving. Custom orders take 15–20 business days and require a 30% advance.' },
    { q: 'What is the return policy for rings at Rings & I?', a: '15-day Perfect Assurance return from the pickup date. Standard rings: 100% refund including GST. Custom rings: 25% deduction. Lab-grown diamonds are non-returnable.' },
    { q: 'Does Rings & I offer payment plans?', a: '30% advance at order confirmation; remaining balance due at pickup. We accept cash, bank transfer, UPI, card, and old gold exchange.' },
  ],
  'Metal & Care': [
    { q: 'What is better — 14 KT or 18 KT gold for rings?', a: '18 KT (75% pure) is recommended for richer colour, more secure settings, and monsoon resistance. 14 KT (58.5% pure) is harder and better suited to active lifestyles. Both are BIS hallmarked.' },
    { q: 'How do I clean a gold ring at home?', a: 'Gently scrub with warm water, mild dish soap, and a soft toothbrush around the setting and band. Rinse thoroughly and pat dry. Avoid harsh chemicals and ultrasonic cleaners.' },
    { q: 'Does silver tarnish? How do I prevent it?', a: 'Silver tarnishes naturally from sulphur and moisture. Prevent tarnish by removing it before bathing, storing in an airtight pouch, and wiping it after each wear. Avoid contact with perfumes and oils.' },
    { q: 'What is the difference between white gold and platinum?', a: 'White gold is yellow gold alloyed with white metals and rhodium-plated — it may need re-plating over time. Platinum is naturally white, denser, hypoallergenic, and develops a rich patina.' },
    { q: 'How long does a diamond ring last?', a: 'Diamonds last a lifetime when properly maintained. We recommend annual professional cleaning and prong checks every 1–2 years at our studios.' },
    { q: 'Is rose gold a good choice for rings in India?', a: 'Yes — a warm, flattering choice. The copper content makes rose gold slightly more durable than yellow gold. Available in 14 KT and 18 KT, all BIS hallmarked.' },
  ],
}

// ─── State ─────────────────────────────────────────────────────────────────

const isMobile = window.innerWidth <= 768

let selectedMetal = 'platinum'
let metalProps    = { color: '#C4C4CE', roughness: 0.06, envMap: 1.6 }
let sec2Shape     = 'emrald'
let sec2Shank     = 'twisted'
let guideC        = null
let guideMetal    = null
let faqCat        = 'Ring Sizing'
let faqOpen       = null
let scrollY       = 0

// GSAP ring animation bridge — plain mutable object
const ringAnim = window.innerWidth <= 768
  ? { x: 0, y: 0.18, scale: 0.76 }
  : { x: 0, y: -0.12, scale: 1.22 }

// ─── Three.js renderer ─────────────────────────────────────────────────────

const canvas = document.getElementById('ring-canvas')

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
renderer.setPixelRatio(
  isMobile ? Math.min(window.devicePixelRatio, 2) : Math.min(window.devicePixelRatio, 1.5)
)
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.68
renderer.outputColorSpace = THREE.SRGBColorSpace
renderer.setClearColor(0x000000, 0)

const scene  = new THREE.Scene()
const camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 100)
camera.position.set(0, 0, 5)

// Studio-like environment from RoomEnvironment (no HDR file needed)
const pmrem  = new THREE.PMREMGenerator(renderer)
const envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
scene.environment = envMap
pmrem.dispose()

// ─── Lights ────────────────────────────────────────────────────────────────

scene.add(new THREE.AmbientLight(0xffffff, 0.55))

const dl1 = new THREE.DirectionalLight(0xfff8f0, 1.6); dl1.position.set(3, 8, 5);   scene.add(dl1)
const dl2 = new THREE.DirectionalLight(0xdce8ff, 0.8); dl2.position.set(-5, 4, -2); scene.add(dl2)
const dl3 = new THREE.DirectionalLight(0xffffff, 0.6); dl3.position.set(6, 2, -4);  scene.add(dl3)

const pl1 = new THREE.PointLight(0xffffff, 1.4, 14, 2); pl1.position.set(0, 5, 3);   scene.add(pl1)
const pl2 = new THREE.PointLight(0xffe4a0, 0.9, 10, 2); pl2.position.set(2, 3, -2);  scene.add(pl2)
const pl3 = new THREE.PointLight(0xb8d4ff, 0.8, 10, 2); pl3.position.set(-2, 3, 2);  scene.add(pl3)

if (!isMobile) {
  const p4 = new THREE.PointLight(0xfff0e8, 0.6, 8, 2);  p4.position.set(0, -2, 4);  scene.add(p4)
  const p5 = new THREE.PointLight(0xffffff, 0.7, 8, 2);  p5.position.set(4, 1, 1);   scene.add(p5)
  const p6 = new THREE.PointLight(0xe8f0ff, 0.6, 8, 2);  p6.position.set(-4, 1, -1); scene.add(p6)
  const p7 = new THREE.PointLight(0xfff5e0, 0.6, 10, 2); p7.position.set(0, -4, 2);  scene.add(p7)
}

// ─── Ring groups ───────────────────────────────────────────────────────────

const groupRef      = new THREE.Group() // GSAP position+scale target
const spinRef       = new THREE.Group() // auto-rotation
const centerGroupRef = new THREE.Group() // XZ centering offset

spinRef.add(centerGroupRef)
groupRef.add(spinRef)
scene.add(groupRef)

let rotY = 0
const ringCenter = { x: 0, z: 0, halfHeight: 0.8 }
const diamondMats = new Set()

// Shared between the animate loop and loadRing's post-load snap — both must
// compute the ring's target Y position identically, or switching shapes/
// shanks snaps to one value and then visibly lerps to the other (a
// bounce-down-then-up on every switch).
const NORMAL_VIEW_LIFT = 0.15
function targetGroupY() {
  return headLocked
    ? (ringAnim.y ?? 0)
    : (-ringCenter.halfHeight + (ringAnim.y ?? 0) + NORMAL_VIEW_LIFT)
}

// True once a stone shape is picked in the Diamond panel — holds the ring's
// yaw facing the viewer instead of auto-rotating, until a Shank option is
// picked or the user scrolls back up out of the customizer.
let headLocked = false

// ─── OrbitControls ─────────────────────────────────────────────────────────

const controls = new OrbitControls(camera, canvas)
controls.enabled = false
controls.enableRotate = false
controls.enableZoom = false
controls.enablePan  = false
controls.minPolarAngle = Math.PI / 5
controls.maxPolarAngle = Math.PI / 1.6

let isInteracting   = false
let isPointerDown   = false
let isReturningHome = false
let hasMovedFromHome = false
let resumeTimer    = null
const HOME_CAMERA_POS = camera.position.clone()
const HOME_TARGET     = controls.target.clone()

function resetCameraToHome() {
  if (!hasMovedFromHome || isReturningHome) return
  hasMovedFromHome = false
  isReturningHome = true

  // Interpolate in spherical coordinates (around the target) rather than
  // lerping x/y/z directly — a straight-line lerp cuts inside the orbit
  // sphere, so the camera briefly gets much closer to the ring partway
  // through (it visibly balloons in size) before swinging back out.
  const from = new THREE.Spherical().setFromVector3(camera.position.clone().sub(controls.target))
  const to   = new THREE.Spherical().setFromVector3(HOME_CAMERA_POS.clone().sub(HOME_TARGET))

  let deltaTheta = to.theta - from.theta
  deltaTheta = ((deltaTheta + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI

  const state = {
    radius: from.radius, phi: from.phi, theta: from.theta,
    tx: controls.target.x, ty: controls.target.y, tz: controls.target.z,
  }

  gsap.to(state, {
    radius: to.radius, phi: to.phi, theta: from.theta + deltaTheta,
    tx: HOME_TARGET.x, ty: HOME_TARGET.y, tz: HOME_TARGET.z,
    duration: 1.6, ease: 'power2.inOut',
    onUpdate() {
      controls.target.set(state.tx, state.ty, state.tz)
      const offset = new THREE.Vector3().setFromSphericalCoords(state.radius, state.phi, state.theta)
      camera.position.copy(controls.target).add(offset)
      controls.update()
    },
    onComplete: () => { isReturningHome = false },
  })
}

controls.addEventListener('start', () => {
  isInteracting = true
  isPointerDown = true
  hasMovedFromHome = true
  clearTimeout(resumeTimer)
  gsap.killTweensOf(camera.position)
  gsap.killTweensOf(controls.target)
  isReturningHome = false
  // pivot the orbit around where the ring actually is right now, not the
  // world origin — otherwise orbiting swings the camera off-axis from the
  // ring and it visibly grows/shrinks as it's dragged
  controls.target.set(groupRef.position.x, groupRef.position.y, 0)
})
controls.addEventListener('end', () => {
  isPointerDown = false
  clearTimeout(resumeTimer)
  resumeTimer = setTimeout(() => {
    isInteracting = false
    resetCameraToHome()
  }, 1800)
})

// ─── Material helpers ──────────────────────────────────────────────────────

function isDiamondMaterial(mat, nodeName = '') {
  const n = (mat.name + nodeName).toLowerCase()
  return (
    n.match(/diamond|gem|stone|crystal|facet|jewel|girdle|culet|crown|pavillion/) ||
    mat.transmission > 0 ||
    (mat.transparent && mat.opacity < 0.92) ||
    (mat.roughness !== undefined && mat.roughness < 0.04 &&
     mat.metalness !== undefined && mat.metalness < 0.05 &&
     mat.color?.r > 0.88)
  )
}

function makeDiamondMat() {
  return new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(1, 1, 1),
    metalness: 0, roughness: 0,
    transmission: 1.0, thickness: 2.2, ior: 2.42, dispersion: 0.3,
    clearcoat: 1.0, clearcoatRoughness: 0.0,
    specularIntensity: 1.0, specularColor: new THREE.Color(1, 1, 1),
    iridescence: 0.65, iridescenceIOR: 2.2,
    iridescenceThicknessRange: [80, 500],
    attenuationColor: new THREE.Color(0.98, 0.98, 1.0),
    attenuationDistance: 0.4,
    envMapIntensity: 3.5,
    side: THREE.FrontSide,
  })
}

// ─── GLB loading ───────────────────────────────────────────────────────────

const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')

const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

let currentModel = null

// ─── Hero cinematic entrance ────────────────────────────────────────────────
// Ring canvas fades in the moment the first model is ready (not a blind timer,
// so it stays smooth regardless of load speed), then the title rises, then the
// description — one sequence, run once.

let heroEntranceStarted = false

// Once the main ring is up and the page has opened, quietly warm the browser
// cache for every other shape/shank combo — one at a time, low priority — so
// switching later in the customizer feels instant instead of re-downloading.
function prefetchOtherRings() {
  if (navigator.connection && navigator.connection.saveData) return

  const queue = []
  for (const shape of SEC2_SHAPES) {
    for (const shank of SEC2_SHANKS) {
      if (shape.id === sec2Shape && shank.id === sec2Shank) continue
      queue.push(GLB_URLS[`${shape.id}_${shank.id}`])
    }
  }

  const next = () => {
    const url = queue.shift()
    if (!url) return
    fetch(url, { priority: 'low' }).catch(() => {}).finally(() => setTimeout(next, 500))
  }
  next()
}

function runHeroEntrance() {
  if (heroEntranceStarted) return
  heroEntranceStarted = true
  setTimeout(prefetchOtherRings, 1500)

  const loadingScreen = document.getElementById('loading-screen')
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (reduceMotion) {
    loadingScreen.remove()
    gsap.set(canvas, { opacity: 1 })
    gsap.set('.hero-line-text', { y: 0, yPercent: 0 })
    gsap.set('.hero-desc-text', { y: 0, yPercent: 0 })
    gsap.set('.hero-corner-btn', { y: 0, yPercent: 0 })
    return
  }

  gsap.to(loadingScreen, {
    opacity: 0, duration: 0.4, ease: 'power1.out',
    onComplete: () => loadingScreen.remove(),
  })

  gsap.timeline()
    .fromTo(canvas, { opacity: 0 }, { opacity: 1, duration: 1.0, ease: 'power2.out' })
    .fromTo('.hero-line-text',
      { y: 0, yPercent: 105 },
      { y: 0, yPercent: 0, duration: 1.05, ease: 'power3.out', stagger: 0.1 },
      '-=0.45')
    .fromTo('.hero-desc-text',
      { y: 0, yPercent: 105 },
      { y: 0, yPercent: 0, duration: 0.85, ease: 'power3.out' },
      '-=0.5')
    .fromTo('.hero-corner-btn',
      { y: 0, yPercent: 105 },
      { y: 0, yPercent: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12 },
      '-=0.55')
}

const ringLoadingEl = document.getElementById('ring-loading')
let ringLoadingTimer = null

function loadRing(shape, shank) {
  const path = GLB_URLS[`${shape}_${shank}`]

  clearTimeout(ringLoadingTimer)
  ringLoadingTimer = setTimeout(() => ringLoadingEl.classList.add('ring-loading--visible'), 200)

  const stopRingLoading = () => {
    clearTimeout(ringLoadingTimer)
    ringLoadingEl.classList.remove('ring-loading--visible')
  }

  gltfLoader.load(path, (gltf) => {
    while (centerGroupRef.children.length) centerGroupRef.remove(centerGroupRef.children[0])
    diamondMats.clear()

    const root = gltf.scene
    root.position.set(0, 0, 0)
    root.rotation.set(0, 0, 0)
    root.scale.set(1, 1, 1)

    root.traverse(node => {
      node.matrixAutoUpdate = true
      if (!node.isMesh) return
      node.material = Array.isArray(node.material)
        ? node.material.map(m => m.clone())
        : node.material.clone()
    })

    // Scale so largest dim = 1.7 world units
    root.updateMatrixWorld(true)
    const box1 = new THREE.Box3().setFromObject(root)
    const size = new THREE.Vector3()
    box1.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z)
    if (maxDim > 0) root.scale.setScalar(1.7 / maxDim)

    // Anchor base at y=0
    root.updateMatrixWorld(true)
    const box2   = new THREE.Box3().setFromObject(root)
    const center = new THREE.Vector3()
    box2.getCenter(center)
    root.position.y -= box2.min.y

    ringCenter.x          = center.x
    ringCenter.z          = center.z
    ringCenter.halfHeight = (box2.max.y - box2.min.y) / 2

    // XZ centering
    centerGroupRef.position.set(-ringCenter.x, 0, -ringCenter.z)

    // Snap groups to current GSAP state — prevents 1-frame pop on shape switch
    groupRef.position.x = ringAnim.x
    groupRef.position.y = targetGroupY()
    groupRef.scale.setScalar(ringAnim.scale)

    // Upgrade materials: detect diamonds + set initial env intensity
    root.traverse(node => {
      if (!node.isMesh) return
      const upgrade = mat => {
        if (isDiamondMaterial(mat, node.name)) {
          const gem = makeDiamondMat()
          diamondMats.add(gem)
          return gem
        }
        mat.envMapIntensity = 0.03
        if (mat.roughness !== undefined && mat.roughness < 0.42) mat.roughness = 0.2
        mat.needsUpdate = true
        return mat
      }
      node.material = Array.isArray(node.material)
        ? node.material.map(upgrade)
        : upgrade(node.material)
    })

    centerGroupRef.add(root)
    currentModel = root
    applyMetal(metalProps)
    runHeroEntrance()
    stopRingLoading()
  },
  undefined,
  (err) => { console.error('[BYOR] GLB load failed:', err); runHeroEntrance(); stopRingLoading() }
  )
}

function applyMetal(props) {
  if (!currentModel) return
  const color = new THREE.Color(props.color)
  currentModel.traverse(node => {
    if (!node.isMesh) return
    const mats = Array.isArray(node.material) ? node.material : [node.material]
    mats.forEach(mat => {
      if (diamondMats.has(mat)) return
      // Some exported GLBs (e.g. the vintage twisted shank) mis-tag a metal
      // prong material as non-metallic with a baked-in tint — catch those by
      // name so they still follow the selected metal instead of staying stuck.
      const isMetalPart = (mat.metalness !== undefined && mat.metalness > 0.1) || /metal|prong/i.test(mat.name || '')
      if (isMetalPart) {
        mat.color.set(color)
        mat.metalness = 1.0
        mat.roughness = props.roughness
        mat.envMapIntensity = props.envMap
        mat.needsUpdate = true
      }
    })
  })
}

// ─── Animation loop ────────────────────────────────────────────────────────

let prevTime = performance.now()

function animate(now) {
  requestAnimationFrame(animate)

  const delta = Math.min((now - prevTime) / 1000, 0.05)
  prevTime = now

  const scrollRot = scrollY * 0.003

  if (headLocked) {
    // Ease the yaw toward 0 (head-on) via the shortest angular path, rather
    // than continuing the auto-rotate accumulator — this is what holds the
    // diamond facing the viewer once a stone shape is selected. rotY stays
    // synced to the current visual angle every frame so that unlocking
    // later (Shank picked, or scrolled back up) resumes auto-rotation from
    // right here with no jump.
    const TWO_PI = Math.PI * 2
    let normalized = ((spinRef.rotation.y % TWO_PI) + TWO_PI) % TWO_PI
    if (normalized > Math.PI) normalized -= TWO_PI
    spinRef.rotation.y = normalized * (1 - 0.08)
    rotY = spinRef.rotation.y - scrollRot
  } else if (!isInteracting) {
    rotY -= delta * 0.12
    spinRef.rotation.y = rotY + scrollRot
  } else {
    rotY = spinRef.rotation.y - scrollRot
  }

  // Lerp toward GSAP-animated ringAnim values
  const tx = ringAnim.x
  const ty = targetGroupY()
  const ts = ringAnim.scale

  groupRef.position.x += (tx - groupRef.position.x) * 0.12
  groupRef.position.y += (ty - groupRef.position.y) * 0.12
  const cs = groupRef.scale.x
  groupRef.scale.setScalar(cs + (ts - cs) * 0.12)

  // Ring dragging is disabled entirely (controls.enabled is permanently
  // false — see OrbitControls setup above).

  // Tilt the ring's head toward the viewer — same tilt in the hero as in
  // section 2 (customizer) now, so the head doesn't visibly reorient
  // between sections. Tilted much further flat (near-overhead, "flat lay"
  // product-shot angle) once a diamond shape is locked, same camera
  // distance, no zoom change.
  const targetTiltX = headLocked ? 1.5 : 0.1
  spinRef.rotation.x += (targetTiltX - spinRef.rotation.x) * 0.12
  if (isMobile) {
    canvas.style.touchAction = 'pan-y'
  }
  controls.update()

  renderer.render(scene, camera)
}

requestAnimationFrame(animate)

// ─── Resize ────────────────────────────────────────────────────────────────

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})

// ─── SVG icon helpers ──────────────────────────────────────────────────────

function shapeIconSVG(id) {
  const shapes = {
    round:    `<circle cx="12" cy="12" r="8" />`,
    pearl:    `<path d="M12 5c-2.2 0-4.2.9-5.6 2.5C5 9 4.5 10.8 4.5 12.5c0 3.2 1.6 5.8 4 7.2L12 22l3.5-2.3c2.4-1.4 4-4 4-7.2 0-1.7-.5-3.5-1.9-5C16.2 5.9 14.2 5 12 5z" />`,
    emrald:   `<rect x="3.5" y="6" width="17" height="12" rx="1.5" /><line x1="3.5" y1="9" x2="20.5" y2="9" stroke-width="0.8" /><line x1="3.5" y1="15" x2="20.5" y2="15" stroke-width="0.8" />`,
    princess: `<rect x="4.5" y="4.5" width="15" height="15" rx="1" />`,
    vintage:  `<circle cx="12" cy="12" r="7" /><circle cx="12" cy="4.3" r="0.9" fill="currentColor" stroke="none" /><circle cx="12" cy="19.7" r="0.9" fill="currentColor" stroke="none" /><circle cx="4.3" cy="12" r="0.9" fill="currentColor" stroke="none" /><circle cx="19.7" cy="12" r="0.9" fill="currentColor" stroke="none" />`,
  }
  return `<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">${shapes[id] ?? shapes.round}</svg>`
}

function shankIconSVG(id) {
  const icons = {
    classic: `<ellipse cx="12" cy="12" rx="9" ry="6" />`,
    channel: `<ellipse cx="12" cy="12" rx="9" ry="6" /><ellipse cx="12" cy="12" rx="6" ry="3.5" stroke-dasharray="2 1.5" />`,
    cave:    `<ellipse cx="12" cy="12" rx="9" ry="6" /><ellipse cx="12" cy="12" rx="4.5" ry="2.5" />`,
    split:   `<path d="M3 9 Q12 12 21 9" /><path d="M3 15 Q12 12 21 15" />`,
    twisted: `<path d="M3 9 Q7.5 12 12 9 Q16.5 6 21 9" /><path d="M3 15 Q7.5 12 12 15 Q16.5 18 21 15" /><path d="M3 12 Q7.5 9 12 12 Q16.5 15 21 12" stroke-dasharray="1 1.5" />`,
  }
  return `<svg width="44" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icons[id]}</svg>`
}

// ─── DOM builders ──────────────────────────────────────────────────────────

function buildShapeSelector() {
  const list = document.getElementById('shape-list')
  list.innerHTML = ''
  SEC2_SHAPES.forEach(s => {
    const btn = document.createElement('button')
    btn.className = `rb-option-btn${sec2Shape === s.id ? ' rb-option-btn--active' : ''}`
    btn.setAttribute('aria-label', s.desc)
    btn.setAttribute('aria-pressed', String(sec2Shape === s.id))
    btn.dataset.id = s.id
    btn.innerHTML = `${shapeIconSVG(s.id)}<div class="rb-option-text"><span class="rb-option-label">${s.label}</span><span class="rb-option-desc">${s.desc}</span></div>`
    btn.addEventListener('click', () => {
      sec2Shape = s.id
      setHeadLocked(true)
      loadRing(sec2Shape, sec2Shank)
      list.querySelectorAll('.rb-option-btn').forEach(b => {
        const active = b.dataset.id === s.id
        b.classList.toggle('rb-option-btn--active', active)
        b.setAttribute('aria-pressed', String(active))
      })
    })
    list.appendChild(btn)
  })
}

function buildShankSelector() {
  const list = document.getElementById('shank-list')
  list.innerHTML = ''
  SEC2_SHANKS.forEach(s => {
    const btn = document.createElement('button')
    btn.className = `rb-option-btn${sec2Shank === s.id ? ' rb-option-btn--active' : ''}`
    btn.setAttribute('aria-label', s.desc)
    btn.setAttribute('aria-pressed', String(sec2Shank === s.id))
    btn.dataset.id = s.id
    btn.innerHTML = `${shankIconSVG(s.id)}<div class="rb-option-text"><span class="rb-option-label">${s.label}</span><span class="rb-option-desc">${s.desc}</span></div>`
    btn.addEventListener('click', () => {
      sec2Shank = s.id
      setHeadLocked(false)
      loadRing(sec2Shape, sec2Shank)
      list.querySelectorAll('.rb-option-btn').forEach(b => {
        const active = b.dataset.id === s.id
        b.classList.toggle('rb-option-btn--active', active)
        b.setAttribute('aria-pressed', String(active))
      })
    })
    list.appendChild(btn)
  })
}

function buildMetalSelector() {
  const opts = document.getElementById('metal-options')
  opts.innerHTML = ''
  METALS.forEach(m => {
    const btn = document.createElement('button')
    btn.className = `rb-metal-opt${selectedMetal === m.id ? ' active' : ''}`
    btn.setAttribute('aria-label', m.label)
    btn.setAttribute('aria-pressed', String(selectedMetal === m.id))
    btn.dataset.id = m.id
    btn.innerHTML = `<span class="rb-metal-dot" style="background:${m.dot}"></span><span class="rb-metal-opt-label">${m.label}</span>`
    btn.addEventListener('click', () => {
      selectedMetal = m.id
      metalProps    = { color: m.color, roughness: m.roughness, envMap: m.envMap }
      applyMetal(metalProps)
      opts.querySelectorAll('.rb-metal-opt').forEach(b => {
        const active = b.dataset.id === m.id
        b.classList.toggle('active', active)
        b.setAttribute('aria-pressed', String(active))
      })
    })
    opts.appendChild(btn)
  })
}

function buildDiamondGuide() {
  const container = document.getElementById('diamond-guide')
  container.innerHTML = ''
  FOUR_CS.forEach((c, i) => {
    const btn = document.createElement('button')
    btn.className = 'ga-row'
    btn.dataset.id = c.id
    btn.innerHTML = `<div class="ga-row-head">
      <span class="ga-num">${String(i + 1).padStart(2, '0')}</span>
      <span class="ga-label">${c.label}</span>
      <span class="ga-tagline">${c.tagline}</span>
      <svg class="ga-arrow" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><polyline points="9,18 15,12 9,6" /></svg>
    </div>`
    btn.addEventListener('click', () => {
      guideC = guideC === c.id ? null : c.id
      container.querySelectorAll('.ga-row').forEach(r => {
        const rowC  = FOUR_CS.find(x => x.id === r.dataset.id)
        const isOn  = r.dataset.id === guideC
        r.classList.toggle('ga-row--active', isOn)
        const existing = r.querySelector('.ga-info')
        if (isOn && !existing) {
          const p = document.createElement('p'); p.className = 'ga-info'; p.textContent = rowC.info; r.appendChild(p)
        } else if (!isOn && existing) {
          r.removeChild(existing)
        }
      })
    })
    container.appendChild(btn)
  })
}

function buildMetalGuide() {
  const container = document.getElementById('metal-guide')
  container.innerHTML = ''
  METALS_EDU.forEach(m => {
    const btn = document.createElement('button')
    btn.className = 'ga-row'
    btn.dataset.id = m.id
    btn.innerHTML = `<div class="ga-row-head">
      <div class="ga-disc" style="background:${m.gradient}"></div>
      <span class="ga-label">${m.name}</span>
      <span class="ga-tagline">${m.tagline}</span>
      <svg class="ga-arrow" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><polyline points="9,18 15,12 9,6" /></svg>
    </div>`
    btn.addEventListener('click', () => {
      guideMetal = guideMetal === m.id ? null : m.id
      container.querySelectorAll('.ga-row').forEach(r => {
        const rowM  = METALS_EDU.find(x => x.id === r.dataset.id)
        const isOn  = r.dataset.id === guideMetal
        r.classList.toggle('ga-row--active', isOn)
        const existing = r.querySelector('.ga-info')
        if (isOn && !existing) {
          const p = document.createElement('p'); p.className = 'ga-info'; p.textContent = rowM.desc; r.appendChild(p)
        } else if (!isOn && existing) {
          r.removeChild(existing)
        }
      })
    })
    container.appendChild(btn)
  })
}

function buildFAQCats() {
  const cats = document.getElementById('faq-cats')
  cats.innerHTML = ''
  FAQ_CATS.forEach(cat => {
    const btn = document.createElement('button')
    btn.className = `faq-cat-btn${faqCat === cat ? ' faq-cat-btn--active' : ''}`
    btn.setAttribute('role', 'tab')
    btn.setAttribute('aria-selected', String(faqCat === cat))
    btn.textContent = cat
    btn.addEventListener('click', () => {
      faqCat = cat
      faqOpen = null
      cats.querySelectorAll('.faq-cat-btn').forEach(b => {
        const active = b.textContent === cat
        b.classList.toggle('faq-cat-btn--active', active)
        b.setAttribute('aria-selected', String(active))
      })
      buildFAQList()
    })
    cats.appendChild(btn)
  })
}

function buildFAQList() {
  const list = document.getElementById('faq-list')
  list.innerHTML = ''
  FAQ_DATA[faqCat].forEach((item, i) => {
    const div = document.createElement('div')
    div.className = 'faq-item'
    div.innerHTML = `
      <button class="faq-q" aria-expanded="false">
        <span>${item.q}</span>
        <svg class="faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      <div class="faq-a" aria-hidden="true"><p>${item.a}</p></div>`
    div.querySelector('.faq-q').addEventListener('click', () => {
      faqOpen = faqOpen === i ? null : i
      list.querySelectorAll('.faq-item').forEach((el, j) => {
        const open = faqOpen === j
        el.classList.toggle('faq-item--open', open)
        el.querySelector('.faq-q').setAttribute('aria-expanded', String(open))
        el.querySelector('.faq-a').setAttribute('aria-hidden', String(!open))
      })
    })
    list.appendChild(div)
  })
}

// ─── Init DOM ──────────────────────────────────────────────────────────────

buildShapeSelector()
buildShankSelector()
buildMetalSelector()
buildDiamondGuide()
buildMetalGuide()
buildFAQCats()
buildFAQList()

// Load default ring
loadRing(sec2Shape, sec2Shank)

// Safety net: never leave the loading screen stuck if the model request hangs
setTimeout(runHeroEntrance, 8000)

// ─── Hero buttons ──────────────────────────────────────────────────────────

const heroEl          = document.getElementById('hero')
const customizerEl    = document.getElementById('customizer')
// Section 4 (Guide), not the true last section (FAQ) — the ring background
// is meant to stop appearing once the Guide section ends, so FAQ always
// shows on its own solid background with no sticky canvas behind it.
const bgBoundaryEl    = document.querySelector('.guide')
const canvasWrapperEl = document.querySelector('.canvas-wrapper')

// ─── Head View switch ───────────────────────────────────────────────────────
// Keeps the manual toggle and headLocked in sync regardless of what changed
// it — picking a diamond shape, picking a shank, scrolling back up, or the
// switch itself.
const headViewToggle = document.getElementById('head-view-toggle')
function setHeadLocked(value) {
  headLocked = value
  headViewToggle.checked = value
}
headViewToggle.addEventListener('change', () => {
  setHeadLocked(headViewToggle.checked)
})

document.getElementById('start-designing-btn').addEventListener('click', () => {
  lenis.scrollTo(customizerEl)
})

// ─── Lenis + GSAP smooth scroll ────────────────────────────────────────────

window.history.scrollRestoration = 'manual'
// Scroll to this section's own top, not the page's — on Shopify the theme's
// header/announcement bar sits above this section, so scrolling to 0 would
// leave the hero only partially visible (its bottom pushed off-screen,
// showing a sliver of the customizer underneath instead of a clean full
// hero). Aligning to heroEl's real top guarantees the hero fills the screen
// on load regardless of whatever chrome the theme places above it.
window.scrollTo(0, 0)
window.scrollTo(0, heroEl.getBoundingClientRect().top)

const lenis = new Lenis({ lerp: 0.08, smoothWheel: true })

gsap.registerPlugin(ScrollTrigger)
gsap.ticker.add((time) => lenis.raf(time * 1000))
gsap.ticker.lagSmoothing(0)
lenis.on('scroll', ScrollTrigger.update)

let lastScrollY = 0
let scrollSettleTimer = null

// ─── Hero ↔ Customizer section snap ────────────────────────────────────────
// Section 1 (hero) and section 2 (customizer) are both a full 100vh, so
// leaving the viewport straddling both looks unfinished. Once the user's
// scroll gesture settles anywhere inside that zone, commit the rest of the
// way to whichever section they were heading toward — same behaviour on
// mobile and desktop.
//
// Measured from the real elements rather than assuming the hero starts at
// scroll 0 — on Shopify, the theme's own header/subnav sits above this
// section (measured ~153px on the live site), so "scroll 0" is actually
// above the hero, and "one viewport height" overshoots short of the
// customizer's real top. Reading getBoundingClientRect().top + scroll gives
// each section's true absolute position regardless of what sits above it.
let snapSettleTimer = null
let isSnapping = false

lenis.on('scroll', ({ scroll }) => {
  const scrollingUp = scroll < lastScrollY
  const scrollDirection = scrollingUp ? -1 : 1
  lastScrollY = scroll
  scrollY = scroll

  if (scrollingUp && scroll < window.innerHeight * 0.5 && !isPointerDown) {
    // wait for the scroll gesture to actually finish before springing back,
    // rather than reacting mid-scroll
    clearTimeout(scrollSettleTimer)
    scrollSettleTimer = setTimeout(resetCameraToHome, 300)
  } else {
    clearTimeout(scrollSettleTimer)
  }

  const heroTop       = heroEl.getBoundingClientRect().top + scroll
  const customizerTop = customizerEl.getBoundingClientRect().top + scroll

  // Scrolling back up out of the customizer releases the head-lock (see
  // headLocked above) — the ring eases back into its normal auto-rotating
  // view instead of staying frozen facing the viewer.
  if (headLocked && scrollingUp && scroll < customizerTop) {
    setHeadLocked(false)
  }

  clearTimeout(snapSettleTimer)
  if (!isSnapping && scroll > heroTop && scroll < customizerTop) {
    snapSettleTimer = setTimeout(() => {
      isSnapping = true
      lenis.scrollTo(scrollDirection > 0 ? customizerTop : heroTop, {
        duration: 0.8,
        easing: t => 1 - Math.pow(1 - t, 4),
        onComplete: () => { isSnapping = false },
      })
    }, 120)
  }

  // The sticky canvas (height:100vh + margin-bottom:-100vh) needs a full
  // extra viewport height of scroll past wherever it stops being wanted
  // before it finishes retracting on its own — left alone that shows as
  // the ring background bleeding into whatever comes right after. Force-
  // hide it the instant section 4 (Guide) has fully scrolled past, so it
  // never shows behind FAQ or anything past this section either.
  const bgBoundaryInViewport = bgBoundaryEl.getBoundingClientRect().bottom
  canvasWrapperEl.style.visibility = bgBoundaryInViewport <= 0 ? 'hidden' : 'visible'
})

// ─── Customizer panels slide-in (replaces Framer Motion whileInView) ───────

gsap.fromTo('#diamond-panel',
  { opacity: 0, x: -22 },
  { opacity: 1, x: 0, duration: 0.75, ease: 'power2.out',
    scrollTrigger: { trigger: '#diamond-panel', start: 'top 75%', once: true } }
)
gsap.fromTo('#shank-panel',
  { opacity: 0, x: 22 },
  { opacity: 1, x: 0, duration: 0.75, ease: 'power2.out',
    scrollTrigger: { trigger: '#shank-panel', start: 'top 75%', once: true } }
)

// ─── Ring scroll animation (GSAP animates ringAnim object) ─────────────────

const mm = gsap.matchMedia()

// Desktop hero starts at the same position/scale the ring settles into for
// section 2 (customizer) — so there's no repositioning "travel" as you
// scroll from section 1 into section 2, the ring already sits where it'll
// end up. Mobile can't do the same: customizer's y:0.95 deliberately pushes
// the ring up top to clear the Diamond/Shank/Metal panels below it, but the
// hero has no such panels — its title text sits centered on screen, so that
// same position makes the ring completely cover "Build Your Own Ring".
// Mobile hero keeps its own lower position instead.
mm.add('(max-width: 768px)', () => {
  Object.assign(ringAnim, { x: 0, y: 0.18, scale: 0.76 })
  gsap.fromTo(ringAnim,
    { x: 0, y: 0.18, scale: 0.76 },
    { x: 0, y: 0.95, scale: 0.76, ease: 'none',
      scrollTrigger: { trigger: '#customizer', start: 'top 80%', end: 'top 10%', scrub: 0.35, invalidateOnRefresh: true } }
  )
})

mm.add('(min-width: 769px)', () => {
  Object.assign(ringAnim, { x: 0, y: 0.24, scale: 0.92 })
  gsap.fromTo(ringAnim,
    { x: 0, y: 0.24, scale: 0.92 },
    { x: 0, y: 0.24, scale: 0.92, ease: 'none',
      scrollTrigger: { trigger: '#customizer', start: 'top 80%', end: 'top 10%', scrub: 0.35, invalidateOnRefresh: true } }
  )
})

// ─── Hero text + description fade on scroll ─────────────────────────────────

gsap.fromTo(['.hero-bg-text', '.hero-desc'],
  { opacity: 1 },
  { opacity: 0, ease: 'none',
    scrollTrigger: { trigger: '#hero', start: 'bottom 70%', end: 'bottom 10%', scrub: 0.5, invalidateOnRefresh: true } }
)

// immediateRender:false so this doesn't fight the buttons' own load-in entrance
// animation (both touch opacity) — it only takes over once the user scrolls
gsap.fromTo(['#hero-btn-bl', '#hero-btn-br'],
  { opacity: 1 },
  { opacity: 0, ease: 'none', immediateRender: false,
    scrollTrigger: { trigger: '#hero', start: 'bottom 70%', end: 'bottom 10%', scrub: 0.5, invalidateOnRefresh: true } }
)

// ─── ScrollTrigger safety refresh ──────────────────────────────────────────
// The ring's scroll-scrubbed scale (mm.add blocks above) is driven by
// #customizer's measured position at the moment each ScrollTrigger is
// created. On Shopify, the theme's header/footer and third-party app scripts
// (chat widgets, cookie banners, pixels — see the blocked-request noise from
// earlier debugging) can inject or resize content above/below this section
// *after* that measurement, leaving stale start/end pixel values that
// suddenly "correct" mid-scroll — visible as the ring jumping bigger/smaller.
// This doesn't reproduce in local vanilla testing since there's no
// surrounding theme chrome to shift layout after load. Re-running refresh()
// after the window fully loads, and again a couple of times shortly after
// (to catch anything that injects asynchronously), keeps trigger positions
// accurate without needing a MutationObserver.
window.addEventListener('load', () => ScrollTrigger.refresh())
setTimeout(() => ScrollTrigger.refresh(), 2000)
setTimeout(() => ScrollTrigger.refresh(), 5000)
